import { PayloadAction } from "@reduxjs/toolkit";

import { DesignData, SpreadGroundImage } from "editor/src/store/design/types";
import getDesignKey from "editor/src/store/variants/helpers/getDesignKey";
import { makeGroupKey } from "editor/src/store/variants/helpers/groupVariantsBy";
import getSpreadGroupsForGivenVariant from "editor/src/store/variants/selector/getSpreadGroupsForGivenVariant";
import { VariantsState } from "editor/src/store/variants/types";

const addDesignTemplatesReducer = (
  state: VariantsState,
  action: PayloadAction<
    Array<{
      designData: DesignData;
      spreadBackgrounds?: SpreadGroundImage[];
      spreadForegrounds?: SpreadGroundImage[];
    }>
  >,
) => {
  action.payload.forEach(
    ({ designData, spreadForegrounds, spreadBackgrounds }) => {
      // The host answers positionally and leaves an entry empty when it has
      // no template for that variant.
      if (!designData) return;
      const pageCount = designData.page_count_limit
        ? designData.page_count
        : undefined;
      const designKey = getDesignKey(designData.product_uid, {
        dimensions: designData.dimensions,
        relatedDimensions: designData.related_dimensions,
        pageCount,
      });
      const spreadGroups = getSpreadGroupsForGivenVariant(
        state,
        designData.product_uid,
      );
      state.designTemplates[designKey] = {
        ...designData,
        spread_groups: spreadGroups,
      };

      const noParamDesignKey = getDesignKey(designData.product_uid, {
        dimensions: undefined,
        pageCount: undefined,
      });
      if (!state.designTemplates[noParamDesignKey]) {
        state.designTemplates[noParamDesignKey] = designData;
      }

      if (spreadBackgrounds && spreadForegrounds) {
        // GAPI-30848 — MERGE new scenes into the existing entry for the same
        // key instead of REPLACING.
        //
        // Why: variant groupings share a `groupKey` (e.g. all sizes of one
        // colour collapse onto "Athletic Heather"). The first variant whose
        // template lands populates `spreadImages[groupKey]` — often the host
        // augmented that response with secondary-tech scenes (`back-dtg`)
        // so multi-tech canvas + bottom-strip thumbnails render.
        //
        // When a second size of the same colour was added later, the iframe
        // re-fired `requestDesignTemplates`; the host returned the new
        // size's pristine catalogue scenes (NO `back-dtg`, because we now
        // only augment templates matching the ACTIVE uid). A REPLACE wiped
        // the augmented `back-dtg` off the active colour's spreadImages —
        // the canvas + Back tile went blank, even though the new size's
        // scenes are otherwise identical to the existing ones.
        //
        // Merging: new scenes take priority for their spread names (the
        // catalogue may have updated per-size mockups); existing scenes for
        // spread names NOT in the new payload survive (preserves the
        // augmented `back-dtg`). Same-name entries dedupe to the new one.
        const mergeScenes = (
          next: SpreadGroundImage[],
          prev: SpreadGroundImage[] | undefined,
        ): SpreadGroundImage[] => {
          if (!prev?.length) return next;
          const nextNames = new Set(
            next.map((s) => s?.name).filter(Boolean) as string[],
          );
          const preserved = prev.filter(
            (s) => s?.name && !nextNames.has(s.name),
          );
          return preserved.length ? [...next, ...preserved] : next;
        };
        const variation = state.product.productVariations.find(
          (variation) => variation.productUid === designData.product_uid,
        );
        if (variation) {
          const groupKey = makeGroupKey(
            {
              variation,
              dimensions: designData.dimensions,
              pageCount: designData.page_count,
            },
            state.product.groupBy,
          );
          const existing = state.spreadImages[groupKey];
          state.spreadImages[groupKey] = {
            spreadBackgrounds: mergeScenes(
              spreadBackgrounds,
              existing?.spreadBackgrounds,
            ),
            spreadForegrounds: mergeScenes(
              spreadForegrounds,
              existing?.spreadForegrounds,
            ),
          };

          const designOptionController = state.designOptionsControl?.[0];
          if (designOptionController) {
            designOptionController?.options.forEach((option) => {
              const optKey = `${groupKey}-${option.value}`;
              const optExisting = state.spreadImages[optKey];
              state.spreadImages[optKey] = {
                spreadBackgrounds: mergeScenes(
                  spreadBackgrounds,
                  optExisting?.spreadBackgrounds,
                ),
                spreadForegrounds: mergeScenes(
                  spreadForegrounds,
                  optExisting?.spreadForegrounds,
                ),
              };
            });
          }
        }
        const existingByDesignKey = state.spreadImages[designKey];
        state.spreadImages[designKey] = {
          spreadBackgrounds: mergeScenes(
            spreadBackgrounds,
            existingByDesignKey?.spreadBackgrounds,
          ),
          spreadForegrounds: mergeScenes(
            spreadForegrounds,
            existingByDesignKey?.spreadForegrounds,
          ),
        };
        const existingByNoParam = state.spreadImages[noParamDesignKey];
        state.spreadImages[noParamDesignKey] = {
          spreadBackgrounds: mergeScenes(
            spreadBackgrounds,
            existingByNoParam?.spreadBackgrounds,
          ),
          spreadForegrounds: mergeScenes(
            spreadForegrounds,
            existingByNoParam?.spreadForegrounds,
          ),
        };
      }
    },
  );
};

export default addDesignTemplatesReducer;
